import {
  Bell,
  CheckSquare,
  Clock,
  FileText,
  Gift,
  GitBranch,
  Key,
  Mail,
  Play,
  Send,
  Shield,
  Users,
  Webhook,
  XCircle,
  Calendar,
} from 'lucide-react';

/**
 * Platform-specific workflow action groups merged into WorkflowActionsPanel via extraGroups.
 */
export const PLATFORM_ACTION_EXTRA_GROUPS = [
  {
    id: 'postgrid',
    name: 'PostGrid / Mail Delivery',
    actions: [
      {
        id: 'postgrid_action',
        name: 'PostGrid Action',
        label: 'PostGrid Action',
        description: 'Update mail status, sync delivery, update metrics, manage credits',
        icon: Mail,
        color: 'blue',
        isConsolidated: true,
        actionTypes: [
          { value: 'update_status', label: 'Update Mail Status' },
          { value: 'sync_status', label: 'Sync PostGrid Status' },
          { value: 'mark_mailed', label: 'Mark Letter as Mailed' },
          { value: 'mark_failed', label: 'Mark Letter as Failed' },
          { value: 'store_object_id', label: 'Store PostGrid Object ID' },
          { value: 'store_failure_reason', label: 'Store Failure Reason' },
          { value: 'create_timeline_event', label: 'Create Delivery Timeline Event' },
          { value: 'update_usage_metrics', label: 'Update Usage Metrics' },
          { value: 'deduct_credits', label: 'Deduct Mailing Credits' },
          { value: 'refund_credits', label: 'Refund Mailing Credits' },
          { value: 'create_admin_task', label: 'Create PostGrid Review Task' },
          { value: 'send_failure_alert', label: 'Send PostGrid Failure Alert' },
        ],
        keywords: ['postgrid', 'mail', 'letter', 'delivery', 'status', 'failed', 'completed', 'credits', 'refund'],
      },
    ],
  },
  {
    id: 'email_actions',
    name: 'Email',
    actions: [
      {
        id: 'email_action',
        name: 'Send Email',
        label: 'Send Email',
        description: 'Send transactional, notification, or alert emails',
        icon: Send,
        color: 'blue',
        isConsolidated: true,
        actionTypes: [
          { value: 'transactional', label: 'Send Transactional Email' },
          { value: 'agency_branded', label: 'Send Agency-Branded Email' },
          { value: 'platform_branded', label: 'Send Platform-Branded Email' },
          { value: 'admin_alert', label: 'Send Admin Alert' },
          { value: 'agency_owner_notification', label: 'Notify Agency Owner' },
          { value: 'user_notification', label: 'Notify User/Client' },
          { value: 'mail_confirmation', label: 'Send Mail Confirmation' },
          { value: 'document_reminder', label: 'Send Document Reminder' },
        ],
        keywords: ['email', 'send', 'notification', 'alert', 'transactional', 'agency', 'platform'],
      },
    ],
  },
  {
    id: 'communication',
    name: 'Communication',
    actions: [
      {
        id: 'send_notification',
        name: 'Internal Alert',
        label: 'Internal Alert',
        description: 'Send internal notification',
        icon: Bell,
        color: 'cyan',
      },
    ],
  },
  {
    id: 'timing',
    name: 'Timing / Flow',
    actions: [
      { id: 'wait', name: 'Wait', label: 'Wait', description: 'Add a time delay', icon: Clock, color: 'amber' },
      {
        id: 'wait_until',
        name: 'Wait Until Date',
        label: 'Wait Until Date',
        description: 'Wait until specific date',
        icon: Calendar,
        color: 'amber',
      },
      {
        id: 'condition',
        name: 'If/Else',
        label: 'If/Else',
        description: 'Add conditional logic',
        icon: GitBranch,
        color: 'purple',
      },
    ],
  },
  {
    id: 'dispute',
    name: 'Dispute Actions',
    actions: [
      {
        id: 'dispute_action',
        name: 'Dispute Action',
        label: 'Dispute Action',
        description: 'Update dispute status, packages, or notify about next steps',
        icon: FileText,
        color: 'purple',
        isConsolidated: true,
        actionTypes: [
          { value: 'update_status', label: 'Update Dispute Status' },
          { value: 'update_package_status', label: 'Update Package Status' },
          { value: 'add_timeline_activity', label: 'Add to Timeline' },
          { value: 'mark_letter_sent', label: 'Mark Letter Sent' },
          { value: 'mark_round_completed', label: 'Mark Round Completed' },
          { value: 'flag_for_review', label: 'Flag for Review' },
          { value: 'request_documents', label: 'Request Supporting Documents' },
          { value: 'notify_next_step', label: 'Notify User Next Step' },
        ],
        keywords: ['dispute', 'package', 'letter', 'status', 'round', 'review'],
      },
    ],
  },
  {
    id: 'crm',
    name: 'CRM / Contact Actions',
    actions: [
      {
        id: 'crm_action',
        name: 'CRM Action',
        label: 'CRM Action',
        description: 'Create, update, or manage contacts and tasks',
        icon: Users,
        color: 'indigo',
        isConsolidated: true,
        actionTypes: [
          { value: 'create_contact', label: 'Create Contact' },
          { value: 'update_contact', label: 'Update Contact' },
          { value: 'add_tag', label: 'Add Tag' },
          { value: 'remove_tag', label: 'Remove Tag' },
          { value: 'add_note', label: 'Add Note' },
          { value: 'create_task', label: 'Create Task' },
          { value: 'update_status', label: 'Update Contact Status' },
          { value: 'add_timeline_event', label: 'Add Timeline Event' },
        ],
        keywords: ['contact', 'crm', 'tag', 'task', 'status', 'note'],
      },
    ],
  },
  {
    id: 'membership_portal',
    name: 'Membership / Portal',
    actions: [
      {
        id: 'grant_portal_access',
        name: 'Grant Portal Access',
        label: 'Grant Portal Access',
        description: 'Enable client portal access',
        icon: Key,
        color: 'emerald',
      },
      {
        id: 'revoke_portal_access',
        name: 'Revoke Portal Access',
        label: 'Revoke Portal Access',
        description: 'Disable portal access',
        icon: Shield,
        color: 'red',
      },
      {
        id: 'grant_offer_access',
        name: 'Grant Offer / Product',
        label: 'Grant Offer / Product',
        description: 'Grant access to offer or product',
        icon: Gift,
        color: 'emerald',
      },
      {
        id: 'revoke_offer_access',
        name: 'Revoke Offer / Product',
        label: 'Revoke Offer / Product',
        description: 'Remove offer or product access',
        icon: XCircle,
        color: 'red',
      },
      {
        id: 'send_portal_invite',
        name: 'Send Portal Invite',
        label: 'Send Portal Invite',
        description: 'Send portal invite email',
        icon: Send,
        color: 'blue',
      },
    ],
  },
  {
    id: 'integrations',
    name: 'Integrations / Advanced',
    actions: [
      {
        id: 'webhook_action',
        name: 'Webhook / API Action',
        label: 'Webhook / API Action',
        description: 'Send outbound webhook or API request',
        icon: Webhook,
        color: 'gray',
        isConsolidated: true,
        actionTypes: [
          { value: 'send_webhook', label: 'Send Webhook' },
          { value: 'send_to_crm', label: 'Send to External CRM' },
          { value: 'custom_request', label: 'Custom HTTP Request' },
        ],
        keywords: ['webhook', 'api', 'http', 'request', 'external'],
      },
      {
        id: 'start_workflow',
        name: 'Start Workflow',
        label: 'Start Workflow',
        description: 'Trigger another workflow',
        icon: Play,
        color: 'emerald',
      },
      {
        id: 'stop_workflow',
        name: 'Stop Workflow',
        label: 'Stop Workflow',
        description: 'Stop a running workflow',
        icon: XCircle,
        color: 'red',
      },
    ],
  },
  {
    id: 'platform',
    name: 'Internal / Platform',
    actions: [
      {
        id: 'internal_notification',
        name: 'Internal Notification',
        label: 'Internal Notification',
        description: 'Notify team members',
        icon: Bell,
        color: 'cyan',
      },
      {
        id: 'create_task',
        name: 'Create Task',
        label: 'Create Task',
        description: 'Create a task for platform staff',
        icon: CheckSquare,
        color: 'teal',
      },
    ],
  },
];
